import React, { useEffect, useState } from 'react';
import { suggestVerification, toUserErrorMessage } from '../../../../services/BookkeepingTemplateService';
import PreviewStep from './PreviewStep';

/**
 * Step 4 (PREVIEW) wrapper for new-shape templates.
 *
 * This is INTENTIONALLY thin — its only job is to call the backend
 * /suggest endpoint with the user's filled-in inputs and pre-populate
 * `entry.customJournalEntries` from the result. The full PreviewStep
 * underneath then renders the entries with all its existing UX:
 * editable journal-entry modal, balance display, account-name
 * resolution, attachments preview, etc.
 *
 * Why this wrapper exists rather than baking /suggest into PreviewStep
 * itself: PreviewStep is shape-agnostic by design (it consumes
 * customJournalEntries) and we want to keep it that way. The
 * shape-aware logic — "call /suggest because the body's lines need
 * server-side evaluation" — lives here at the wizard layer.
 */
function NewTemplatePreviewStep({
    entry,
    onChange,
    companyId,
    templates,
    accounts,
    accountsLoading,
    accountsError,
    getAccountByNumber,
    getAccountsByType,
    searchAccounts,
}) {
    const template = entry?.templateData;
    const [suggestError, setSuggestError] = useState(null);

    useEffect(() => {
        if (!template || !template.body || !companyId) return undefined;
        // Wait until every required input has a value — calling
        // /suggest without them returns a 400 we don't want to surface.
        const inputs = template.body.inputs || [];
        const missingRequired = inputs.some(
            (i) => i.required && (
                entry.parameters?.[i.name] === undefined
                || entry.parameters?.[i.name] === ''
                || entry.parameters?.[i.name] === null
            ),
        );
        if (missingRequired) return undefined;

        let cancelled = false;
        setSuggestError(null);
        suggestVerification(companyId, template.template_id, entry.parameters || {})
            .then((r) => {
                if (cancelled) return;
                // Map /suggest result → entry.customJournalEntries shape
                // expected by PreviewStep's transformEntryToVerification.
                // Only seed when balanced; on imbalance the user gets the
                // PreviewStep's existing error UX from balance check.
                if (r && r.ok && Array.isArray(r.lines) && r.lines.length > 0) {
                    const journalEntries = r.lines.map((l) => ({
                        account: l.account,
                        accountName: l.account_name || '',
                        debit: l.side === 'debit' ? l.amount : 0,
                        credit: l.side === 'credit' ? l.amount : 0,
                        description: l.transaction_description || entry.description || '',
                    }));
                    onChange({ ...entry, customJournalEntries: journalEntries });
                } else if (entry.customJournalEntries) {
                    onChange({ ...entry, customJournalEntries: null });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setSuggestError(err);
                if (entry.customJournalEntries) {
                    onChange({ ...entry, customJournalEntries: null });
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, template?.template_id, JSON.stringify(entry.parameters)]);

    return (
        <>
            {suggestError ? (
                <div className="alert alert-danger" role="alert">
                    {toUserErrorMessage(suggestError)}
                </div>
            ) : null}
            <PreviewStep
                entry={entry}
                onChange={onChange}
                templates={templates}
                accounts={accounts}
                accountsLoading={accountsLoading}
                accountsError={accountsError}
                getAccountByNumber={getAccountByNumber}
                getAccountsByType={getAccountsByType}
                searchAccounts={searchAccounts}
            />
        </>
    );
}

export default NewTemplatePreviewStep;
